import { Injectable } from '@angular/core';
import { DataService } from '@app/shared/services/data/data.service';
import { IMessages } from '@app/shared/interfaces/messages';
import { BehaviorSubject, Observable, of, Subject, switchMap } from 'rxjs';
import { IQuery } from '@app/shared/interfaces/query';
import { UtilService } from '@app/shared/services/utils/util.service';
import { IQueryParams } from '@app/shared/interfaces/queryParams';
import { IPaginatedResponse } from '@app/shared/interfaces/paginated-response.interface';
import { UserService } from '@app/admin-station/work-center/user/services/user.service';
import { UserDTO } from '@app/shared/interfaces/user';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export enum MessageState {
  INBOX = 0,
  ARCHIVED = 1,
  TRASH = 2,
}

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class NotificationsCenterService {
  
  private _baseUrl: string;
  private _userId: string;
  
  public refreshUser$: Subject<void>;
  
  public updateQueryParameters$ = new BehaviorSubject<IQueryParams>(null);

  public filteredNotifications$: Observable<IPaginatedResponse<IMessages>> = this.updateQueryParameters$.pipe(
    switchMap((queryParams: IQueryParams) => {
      if (queryParams) {
        return this._dataService.post(`${this._baseUrl}/search`, queryParams);
      }
      return of({ result: [] });
    })
  );

  constructor(
    private _dataService: DataService,
    private _userService: UserService
  ) {
    this.refreshUser$ = new Subject<void>();
    this._baseUrl = 'Notifications';

    this._userService.userLoginData$.pipe(untilDestroyed(this)).subscribe((userData: UserDTO) => {
      if (!userData) return;

      this._userId = userData.userId;
    })
  }

  public getMessageById(id: string): Promise<IMessages> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const url = `${this._baseUrl}/${id}`;
      const message = await this._dataService.get(url).toPromise();
      resolve(message);
    });
  }

  public markAllAsRead(): Observable<any> {
    const url = `${this._baseUrl}/MarkAllAsRead`;
    return this._dataService.post(url);
  }

  public updateMessageReadDate(messagesIds: string[]): Promise<any> {
    const url = `${this._baseUrl}/batch/update/ReadDate/now()`;
    return this._dataService.post(url, messagesIds).toPromise();
  }

  public updateMessageState(messagesIds: string[], state: number): Promise<any> {
    const url = `${this._baseUrl}/batch/update/state/${state}`;
    return this._dataService.post(url, messagesIds).toPromise();
  }

  public deleteMessage(messagesIds: string[]): Promise<any> {
    const url = `${this._baseUrl}/batch/delete`;
    return this._dataService.post(url, messagesIds).toPromise();
  }

  public deleteOneMessage(messagesId: string): Promise<any> {
    const url = `${this._baseUrl}/${messagesId}`;
    return this._dataService.delete(url).toPromise();
  }

  public flagMessage(messagesIds: string[], state: number): Promise<any> {
    const url = `${this._baseUrl}/batch/update/flag/${state}`;
    return this._dataService.post(url, messagesIds).toPromise();
  }

  public getInboxMessages(query: IQuery = {}): Observable<any> {
    if (!query.filter) {
      const userFilter = this.getUserFilter();
      query.filter = `${userFilter},'state':${MessageState.INBOX}`;
    }

    return this._dataService.get(`${this._baseUrl}${UtilService.fillUrl(query) || ''}`);
  }

  public getUnreadMessages(query: IQuery = {}): Observable<any> {
    if (!query.filter) {
      const userFilter = this.getUserFilter();
      query.filter = `${userFilter},'state':${MessageState.INBOX},'readDate':{ $exists: false }`;
    }

    return this._dataService.get(`${this._baseUrl}${UtilService.fillUrl(query) || ''}`);
  }

  public getUnreadMessagesCount(query: IQuery = {}): Observable<any> {
    if (!query.filter) {
      const userFilter = this.getUserFilter();
      query.filter = `${userFilter},'state':${MessageState.INBOX},'readDate':{ $exists: false }`;
    }

    return this._dataService.get(`${this._baseUrl}/count${UtilService.fillUrl(query) || ''}`);
  }

  public getUserFilter(): string {
    return `'userId':'${this._userId}'`;
  }
}
